import {
  laptopSmallMax,
  laptopSmallMin,
  largeTabletMax,
  tabletMax,
} from '@propertypal/shared/src/constants/mediaQueries';
import PropertyNewsLogo from '@propertypal/shared/src/resources/images/best-of-both/pn-logo.svg';
import PropertyPalLogo from '@propertypal/shared/src/resources/logos/PropertyPalLogo.svg';
import platform from '@propertypal/shared/src/utils/platform';
import styled, { css } from 'styled-components';
import { Box } from '../layout';
import { MidHeading, SmallHeading, Text } from '../typography';

const ROW_GAP = 30;
const COLUMN_GAP = 120;
const TAB_COLUMN_GAP = 10;
const DESKTOP_COLS = 3;

export const Container = styled.div`
  padding: 90px 0 40px 0;

  @media (${tabletMax}) {
    padding-top: 60px;
  }
`;

export const Wrapper = styled.div<{ iconsMw?: number }>`
  padding: 0 10px;
  max-width: ${({ iconsMw }) => iconsMw || 1064}px;
  margin: 0 auto;
`;

export const IconItem = styled.li<{
  maxW?: number;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  text-align: center;

  svg {
    max-height: 70px;
    height: 100%;
  }

  & > div {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  ${MidHeading} {
    font-size: 44px;
  }

  ${Text} {
    font-size: 20px;

    ${({ maxW }) =>
      maxW
        ? css`
            margin: 0 auto;
            max-width: ${maxW}px;
          `
        : ''}
  }

  @media (${largeTabletMax}) and (min-width: 769px) {
    ${MidHeading} {
      font-size: 30px;
    }

    ${Text} {
      font-size: 18px;
    }
  }

  @media (${tabletMax}) {
    ${MidHeading} {
      font-size: 34px;
    }
  }
`;

export const IconsGrid = styled.ul`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  justify-content: center;
  gap: ${ROW_GAP}px ${COLUMN_GAP}px;
  width: 100%;
  padding: 0;
  margin: 0 auto;

  ${IconItem} {
    flex-basis: calc((100% - (${DESKTOP_COLS} - 1) * ${COLUMN_GAP}px) / ${DESKTOP_COLS});

    @media (${tabletMax}) {
      flex-basis: 100%;
    }
  }

  @media (${largeTabletMax}) and (min-width: 769px) {
    column-gap: 10px;
    justify-content: space-between;
    ${IconItem} {
      flex-basis: calc((100% - (${DESKTOP_COLS} - 1) * ${TAB_COLUMN_GAP}px) / ${DESKTOP_COLS});
    }
  }

  @media (${tabletMax}) {
    row-gap: 60px;
  }
`;

export const PromoteFeaturesContainer = styled.div<{ noIcons?: boolean; hasContent?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 50px;
  width: 100%;
  max-width: 940px;
  padding: 0 20px;
  margin: ${({ noIcons }) => (noIcons ? 0 : 70)}px auto 0px auto;

  .link-button {
    padding-left: 35px;
    display: block;
  }

  @media (${laptopSmallMin}) {
    gap: 30px;
    flex-direction: row;
    align-items: ${({ hasContent }) => (hasContent ? 'flex-start' : 'center')};

    & > :nth-child(1) {
      flex-basis: 50%;
      width: 50%;
    }
    & > :nth-child(2) {
      flex-basis: 50%;
      width: 50%;
    }
  }

  @media (${laptopSmallMax}) {
    ${Box} {
      margin: 0 auto;
    }
  }
`;

export const PromoteFeaturesImgContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PromoteFeaturesImg = styled.img<{ maxW?: number }>`
  width: 100%;

  ${({ maxW }) =>
    maxW != null
      ? css`
          max-width: ${maxW}px;
          margin: 0 auto;
        `
      : ''}
`;

export const PromoteFeaturesTitle = styled(SmallHeading)`
  color: ${({ theme }) => theme.textDark};
  line-height: 1.3;
`;

export const PromoteFeaturesList = styled.div`
  list-style: none;
  padding: 0;
  margin: 0;

  & > :not(:first-child) {
    margin-top: 15px;
  }

  a {
    color: ${({ theme }) => theme.primary};
    text-decoration: underline;
    cursor: pointer;
    transition: 300ms all;

    &:hover {
      color: ${({ theme }) => theme.backgroundMid};
    }
  }
`;

export const PromoteFeaturesItem = styled.li`
  display: flex;
  flex-direction: row;
  gap: 14px;
  text-align: left;

  svg {
    margin-top: 2px;
    font-size: 24px;
    color: ${({ theme }) => theme.primary};
  }

  p {
    flex: 1;
    line-height: 1.5;
  }
`;

export const IconsFeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const IconsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
  justify-content: ${platform.select('flex-start', 'flex-end')};
  flex-direction: ${platform.select('row', 'row-reverse')};
`;

export const PnLogo = styled(PropertyNewsLogo)`
  max-height: 36px;
  max-width: 173px;
  width: 100%;
  height: 100%;
`;

export const PpLogo = styled(PropertyPalLogo)`
  max-height: 36px;
  max-width: 98px;
  width: 100%;
  height: 100%;
`;

export const MockupContentContainer = styled.div`
  text-align: center;
  max-width: 280px;

  svg {
    max-height: 90px;
    height: 100%;
  }
`;
